import card1 from '@assets/landing/section2/cards/card1.png';
import card2 from '@assets/landing/section2/cards/card2.png';
import card3 from '@assets/landing/section2/cards/card3.png';
import card4 from '@assets/landing/section2/cards/card4.png';
import card5 from '@assets/landing/section2/cards/card5.png';
import card6 from '@assets/landing/section2/cards/card6.png';
import card7 from '@assets/landing/section2/cards/card7.png';
import card8 from '@assets/landing/section2/cards/card8.png';
import icon1 from '@assets/landing/section2/icon1.png';
import icon2 from '@assets/landing/section2/icon2.png';
import icon3 from '@assets/landing/section2/icon3.png';
import { Box } from '@components/ui/atoms/Box';
import { Button } from '@components/ui/atoms/Button';
import { Container } from '@components/ui/atoms/Container';
import { Flex } from '@components/ui/atoms/Flex';
import { Heading } from '@components/ui/atoms/Heading';
import { Image } from '@components/ui/atoms/Image';
import { Text } from '@components/ui/atoms/Text';
import { fadeUp,floatBox1, floatBox2 } from '@shared/utils/animationsUtil';
import { useScrollReveal } from '@shared/utils/useScrollReveal';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { signupPath } from '../auth/urls';

interface FeatureCardProps {
    icon: string;
    title: string;
    description: string;
    highlightText?: string;
}

const FeatureCard: React.FC<FeatureCardProps> = ({ icon, title, description, highlightText }) => {
    const { isVisible, ref: cardRef } = useScrollReveal(0.1);

    // Helper to highlight text in description if provided
    const renderDescription = () => {
        if (!highlightText) return description;

        const parts = description.split(highlightText);

        return (
            <>
                {parts[0]}
                <Text as="span" color="purple.600" fontWeight="700">
                    {highlightText}
                </Text>
                {parts[1]}
            </>
        );
    };

    return (
        <Box
            ref={cardRef}
            flex="1"
            bg="linear-gradient(45deg, #e9eaf9, transparent)"
            borderRadius="2xl"
            p={{ base: "2rem", md: "3rem" }}
            boxShadow="0 10px 40px rgba(0, 0, 0, 0.04)"
            display="flex"
            flexDirection="column"
            alignItems="center"
            textAlign="center"
            transition="transform 0.3s ease, box-shadow 0.3s ease"
            _hover={{ transform: 'translateY(-5px)', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)' }}
            opacity={isVisible ? 1 : 0}
            animation={isVisible ? `${fadeUp} 0.6s ease-out forwards 0s` : "none"}
        >
            <Box mb="2rem" w="80px" h="80px">
                <Image src={icon} alt={title} w="full" h="full" objectFit="contain" />
            </Box>
            <Heading fontSize="xl" fontWeight="700" color="gray.800" mb="1.5rem">
                {title}
            </Heading>
            <Text color="gray.500" fontSize="md" lineHeight="1.6">
                {renderDescription()}
            </Text>
        </Box>
    );
};

export const AboutPayments: React.FC = () => {
    const { isVisible, ref: sectionRef } = useScrollReveal(0.1);

    return (
        <Box pt="8rem" pb="4rem" position="relative" bg="white" overflow="hidden" ref={sectionRef}>
            <Container maxW="container.xl" position="relative" zIndex="1">
                <Box
                    position="absolute"
                    top={{ base: "-5%", lg: "6%" }}
                    right={{ base: "8%", lg: "4%" }}
                    w={{ base: "100px", lg: "350px" }}
                    h={{ base: "100px", lg: "350px" }}
                    bg="primary"
                    borderRadius="3xl"
                    opacity="0.2"
                    animation={`${floatBox1} 8s ease-in-out infinite`}
                />
                <Box
                    position="absolute"
                    top={{ base: "-2%", sm: "-1%", md: "1%", lg: "32%" }}
                    right={{ base: "28%", sm: "22%", md: "16%", lg: "22%" }}
                    w={{ base: "50px", lg: "150px" }}
                    h={{ base: "50px", lg: "150px" }}
                    bg="#c8ffff"
                    borderRadius="2xl"
                    opacity="1"
                    animation={`${floatBox2} 6s ease-in-out infinite`}
                />

                <Flex
                    direction={{ base: 'column', lg: 'row' }}
                    gap="4rem"
                    mb={{ base: "1rem", md: "8rem" }}
                    opacity={isVisible ? 1 : 0}
                    animation={isVisible ? `${fadeUp} 0.8s ease-out forwards` : "none"}
                >
                    <Box flex="1.5">
                        <Flex align="center" gap="0.75rem" mb="2rem">
                            <Box h="18px" w="4px" bg="primary" opacity="0.5" />
                            <Text fontSize="lg" fontWeight="700" color="gray.800">Why Bhutan Payments</Text>
                        </Flex>

                        <Heading
                            fontSize={{ base: '2xl', md: '3xl', lg: '4xl' }}
                            fontWeight="600"
                            lineHeight="1.3"
                            color="gray.800"
                            mb="2.5rem"
                            letterSpacing="-0.01em"
                        >
                            Elevate your payment experience with our cutting-edge technology infrastructure. Unleash the power of seamless transactions, unparalleled security, and user-friendly features.
                        </Heading>

                        <Flex direction={{ base: 'column', md: 'row' }} align={{ base: 'flex-start', md: 'center' }} gap="2rem">
                            <Box maxW="500px">
                                <Text color="gray.600" fontSize="md" fontWeight="600" mb="0.5rem">
                                    Accept payments via Visa, Mastercard, American Express, digital wallets, and ACH, with transaction fees between <Text as="span" color="purple.600" fontWeight="700">2.2% and 4.99%</Text>.
                                </Text>
                            </Box>

                            <RouterLink to={signupPath}>
                                <Button
                                    visual="primary"
                                    size="lg"
                                    px="2rem"
                                    borderRadius="md"
                                >
                                    Open Account
                                </Button>
                            </RouterLink>
                        </Flex>

                        <Text color="gray.400" fontSize="sm" fontWeight="600" mt="1rem">No hidden fees.</Text>

                        <Flex mt="1rem" gap="1.5rem" align="center" flexWrap="wrap">
                            <Box h="38px" w="60px" display="flex" border="1px solid" borderColor="gray.100" borderRadius="md" boxShadow="sm">
                                <Image src={card1} alt="card1" w="full" h="full" objectFit="contain" />
                            </Box>
                            <Box h="38px" w="60px" display="flex" border="1px solid" borderColor="gray.100" borderRadius="md" boxShadow="sm">
                                <Image src={card2} alt="card2" w="full" h="full" objectFit="contain" />
                            </Box>
                            <Box h="38px" w="60px" display="flex" border="1px solid" borderColor="gray.100" borderRadius="md" boxShadow="sm">
                                <Image src={card3} alt="card1" w="full" h="full" objectFit="contain" />
                            </Box>
                            <Box h="38px" w="60px" display="flex" border="1px solid" borderColor="gray.100" borderRadius="md" boxShadow="sm">
                                <Image src={card4} alt="card1" w="full" h="full" objectFit="contain" />
                            </Box>
                            <Box h="38px" w="60px" display="flex" border="1px solid" borderColor="gray.100" borderRadius="md" boxShadow="sm">
                                <Image src={card5} alt="card1" w="full" h="full" objectFit="contain" />
                            </Box>
                            <Box h="38px" w="60px" display="flex" border="1px solid" borderColor="gray.100" borderRadius="md" boxShadow="sm">
                                <Image src={card6} alt="card1" w="full" h="full" objectFit="contain" />
                            </Box>
                            <Box h="38px" w="60px" display="flex" border="1px solid" borderColor="gray.100" borderRadius="md" boxShadow="sm">
                                <Image src={card7} alt="card1" w="full" h="full" objectFit="contain" />
                            </Box>
                            <Box h="38px" w="60px" display="flex" border="1px solid" borderColor="gray.100" borderRadius="md" boxShadow="sm">
                                <Image src={card8} alt="card1" w="full" h="full" objectFit="contain" />
                            </Box>
                        </Flex>
                    </Box>

                    {/* Right side is intentionally empty per design, background elements fill space */}
                    <Box flex="1" />
                </Flex>

                {/* Feature Cards Grid */}
                <Flex direction={{ base: 'column', md: 'row' }} gap="2rem">
                    <FeatureCard
                        icon={icon1}
                        title="Your Collection is Fast"
                        description="Fast, reliable payouts— received within 2–3 business days from the payment date."
                        highlightText="2–3 business days"
                    />
                    <FeatureCard
                        icon={icon2}
                        title="Your Payment is Always On"
                        description="Engineered to deliver 95%+ payment success rates with 99.8%+ platform uptime."
                        highlightText="95%+ payment success rates"
                    />
                    <FeatureCard
                        icon={icon3}
                        title="Your Transfer is Safe"
                        description="Payments are PCI compliant, handled securely and in adherence to industry-standard data protection protocols."
                        highlightText="PCI compliant"
                    />
                </Flex>
            </Container>
        </Box>
    );
};

AboutPayments.displayName = 'AboutPayments';
